<template>
  <ul class="players">
    <li
      v-for="player in players"
      :key="player.id"
      class="player-row"
      :class="{ 'is-auto': isAuto(player.id) }"
    >
      <span class="who">
        <span class="name">{{ player.name }}</span>
        <span
          class="points"
          :class="{
            'is-negative': pointsFor(player.id) < 0,
            'is-zero': pointsFor(player.id) === 0
          }"
        >
          {{ formatPoints(pointsFor(player.id))
          }}<template v-if="isAuto(player.id)">, the rest</template>
        </span>
      </span>
      <button
        type="button"
        class="units-button"
        :class="{ 'is-empty': !allocation.isEntered(player.id) && !isAuto(player.id) }"
        :aria-label="`${player.name}: ${allocation.counts.value[player.id] ?? 0} ${unitName}. Change`"
        @click="openPicker(player.id)"
      >
        {{ allocation.counts.value[player.id] ?? 0 }}
      </button>
    </li>
  </ul>

  <NumberSelectorModal
    :is-open="editingId !== null"
    :max-units="max"
    :current-player-name="editingName"
    :label="pickerLabel"
    :initial-value="currentValue"
    :limit="editingId ? allocation.limitFor(editingId) : null"
    @select="handleSelect"
    @close="closePicker"
  />
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { formatPoints } from '../utils/scores';
import NumberSelectorModal from './NumberSelectorModal.vue';

const props = defineProps({
  players: {
    type: Array,
    required: true
  },
  allocation: {
    type: Object,
    required: true
  },
  max: {
    type: Number,
    required: true
  },
  unitName: {
    type: String,
    required: true
  },
  pointsFor: {
    type: Function,
    required: true
  },
  isOpen: {
    type: Boolean,
    default: true
  }
});

const editingId = ref(null);

watch(
  () => props.isOpen,
  (open) => {
    if (!open) closePicker();
  }
);

const isAuto = (id) =>
  props.allocation.autoId.value === id && props.allocation.counts.value[id] > 0;

const editingName = computed(
  () => props.players.find((player) => player.id === editingId.value)?.name ?? ''
);

const currentValue = computed(() => {
  const id = editingId.value;
  if (!id || !(props.allocation.isEntered(id) || props.allocation.autoId.value === id)) return null;
  return props.allocation.counts.value[id];
});

const pickerLabel = computed(() => {
  if (!editingId.value) return '';
  const left = props.allocation.limitFor(editingId.value);
  return `How many ${props.unitName}? Up to ${left}.`;
});

const openPicker = (id) => {
  editingId.value = id;
};

const closePicker = () => {
  editingId.value = null;
};

const handleSelect = (units) => {
  const id = editingId.value;
  if (!id) return;
  props.allocation.set(id, units);
  closePicker();
};
</script>

<style scoped>
.players {
  list-style: none;
}

.player-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 60px;
  border-bottom: 1px solid var(--card-line);
}

.player-row:last-child {
  border-bottom: none;
}

.who {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}

.name {
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.points {
  font-size: 0.8125rem;
  font-weight: 600;
}

.points.is-zero {
  color: var(--ink-muted);
}

.points.is-negative {
  color: var(--red);
}

.units-button {
  flex: none;
  width: 3.5rem;
  height: 2.75rem;
  border-radius: var(--radius-control);
  border: 1px solid var(--card-line);
  background-color: var(--control);
  color: var(--ink);
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 1.375rem;
}

.units-button:hover {
  border-color: var(--ink-muted);
}

.units-button.is-empty {
  color: var(--ink-muted);
}

.is-auto .units-button {
  border-style: dashed;
  color: var(--ink-muted);
}
</style>
